/* DateManager — Manages delivery date selection and persistence
 * Handles date storage, retrieval, and synchronization across UI elements
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5
 */

// Prevent redeclaration if script is loaded multiple times
if (!window.DateManager) {

class DateManager {
  constructor() {
    this.LOG = '[DateManager]';
    this.VERBOSE = false;
  }

  /**
   * Logs debug messages if verbose mode is enabled
   * @private
   */
  _dbg(...args) {
    if (this.VERBOSE) {
      console.log(this.LOG, '🐛', ...args);
    }
  }

  /**
   * Gets the currently selected delivery date
   * Checks multiple sources in priority order:
   * 1. Global variable (window.kennicott_selected_date)
   * 2. sessionStorage (kennicott_selected_date)
   * 3. sessionStorage (komet_date_selected_iso - legacy)
   * 4. Date slider selected chip
   * 5. Default to first business day after today
   * 
   * @returns {string} ISO date string (YYYY-MM-DD)
   * 
   * Validates: Requirements 4.1, 4.2, 4.3, 4.4
   */
  getSelectedDate() {
    // 1. Check if already set globally
    if (window.kennicott_selected_date) {
      return window.kennicott_selected_date;
    }
    
    // 2. Check sessionStorage for kennicott_selected_date
    try {
      const stored = sessionStorage.getItem('kennicott_selected_date');
      if (stored) {
        window.kennicott_selected_date = stored;
        return stored;
      }
    } catch (e) {
      // sessionStorage might not be available
    }
    
    // 3. Check sessionStorage for date slider's stored date (legacy)
    try {
      const sliderDate = sessionStorage.getItem('komet_date_selected_iso');
      if (sliderDate) {
        window.kennicott_selected_date = sliderDate;
        try { 
          sessionStorage.setItem('kennicott_selected_date', sliderDate); 
        } catch(_) {}
        return sliderDate;
      }
    } catch (e) {
      // sessionStorage might not be available
    }
    
    // 4. Check the date slider's selected chip
    const selectedChip = document.querySelector('.komet-ds__chip[aria-selected="true"]');
    if (selectedChip && selectedChip.dataset.iso) {
      const chipDate = selectedChip.dataset.iso;
      window.kennicott_selected_date = chipDate;
      try { 
        sessionStorage.setItem('kennicott_selected_date', chipDate); 
      } catch(_) {}
      return chipDate;
    }
    
    // 5. Default to first business day after today
    const defaultDate = this.calculateFirstBusinessDay();
    window.kennicott_selected_date = defaultDate;
    try { 
      sessionStorage.setItem('kennicott_selected_date', defaultDate); 
    } catch(_) {}
    this._dbg('📅 No date found, using default first business day:', defaultDate);
    return defaultDate;
  }

  /**
   * Sets the delivery date and persists it
   * Updates both sessionStorage and global variable
   * Dispatches kennicott:dateChanged event
   * 
   * @param {string} date - ISO date string (YYYY-MM-DD)
   * 
   * Validates: Requirements 4.1, 4.2, 4.5
   */
  setSelectedDate(date) {
    if (!date) {
      console.warn(this.LOG, 'Attempted to set empty date');
      return;
    }

    this._dbg('📅 Setting selected date:', date);

    // Update global variable
    window.kennicott_selected_date = date;

    // Update sessionStorage
    try {
      sessionStorage.setItem('kennicott_selected_date', date);
    } catch (e) {
      console.warn(this.LOG, 'Failed to save date to sessionStorage:', e);
    }

    // Sync all date sliders
    this.syncDateSliders(date);

    // Dispatch event for other components
    // Requirement 4.5: Dispatch kennicott:dateChanged event
    window.dispatchEvent(new CustomEvent('kennicott:dateChanged', {
      detail: { 
        date: date, 
        formatted: this._formatDate(date) 
      }
    }));

    this._dbg('✅ Date updated and event dispatched');
  }

  /**
   * Calculates the first business day after today
   * Skips weekends (Saturday and Sunday)
   * Default is 3 business days from today, but 4 if after noon cutoff
   * 
   * @param {number} businessDaysAhead - Number of business days to add (default: auto-calculated based on time)
   * @returns {string} ISO date string (YYYY-MM-DD)
   * 
   * Validates: Requirement 4.4
   */
  calculateFirstBusinessDay(businessDaysAhead = null) {
    let date = new Date();
    
    // If businessDaysAhead not specified, calculate based on cutoff time
    if (businessDaysAhead === null) {
      const currentHour = date.getHours();
      // If after noon (12:00 PM), add an extra day
      businessDaysAhead = currentHour >= 12 ? 4 : 3;
    }
    
    let businessDaysAdded = 0;
    
    // Add business days (skip weekends)
    while (businessDaysAdded < businessDaysAhead) {
      date.setDate(date.getDate() + 1);
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        businessDaysAdded++;
      }
    }
    
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Syncs all date slider UI elements to show the selected date
   * Updates all .komet-ds__chip elements with aria-selected attribute
   * 
   * @param {string} date - ISO date string (YYYY-MM-DD)
   */
  syncDateSliders(date) {
    this._dbg('🔄 Syncing all date sliders to:', date);
    
    // Find all date slider chips
    const allChips = document.querySelectorAll('.komet-ds__chip');
    allChips.forEach(chip => {
      const chipDate = chip.dataset.iso;
      if (chipDate === date) {
        chip.setAttribute('aria-selected', 'true');
      } else {
        chip.setAttribute('aria-selected', 'false');
      }
    });
  }

  /**
   * Formats an ISO date string as MM/DD/YYYY
   * Parses ISO string directly to avoid timezone issues
   * 
   * @private
   * @param {string} isoDate - ISO date string (YYYY-MM-DD)
   * @returns {string} Formatted date (MM/DD/YYYY)
   */
  _formatDate(isoDate) {
    if (!isoDate) return '';
    
    // Parse ISO date string directly (YYYY-MM-DD) to avoid timezone conversion
    const match = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, yyyy, mm, dd] = match;
      return `${mm}/${dd}/${yyyy}`;
    }
    
    // Fallback to Date object if not ISO format
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';
    
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DateManager;
}

// Make available globally
window.DateManager = DateManager;

} // End of DateManager declaration guard
